<script setup lang="ts" generic="T extends CheckFileIdentification | WorkFileIdentification">
import { useArrayEvery, useArraySome } from '@vueuse/core'
import { get2byteLength } from '@workspace/utils/text'
import { useI18n } from 'vue-i18n'

import BaseInput from '~/components/atoms/BaseInput.vue'
import BaseLabel from '~/components/atoms/BaseLabel.vue'
import BaseIconButton from '~/components/foundations/BaseIconButton.vue'
import BaseStack from '~/components/foundations/BaseStack.vue'
import BaseDialog from '~/components/molecules/BaseDialog.vue'
import DatePicker from '~/components/molecules/DatePicker.vue'
import IdentificationNumberInputDialog from '~/components/organisms/IdentificationNumberInputDialog.vue'
import QRCodeReaderDialogConfirm from '~/components/organisms/QRCodeReaderDialogConfirm.vue'
import type { CheckFileIdentification } from '~/entities/checkFileIdentification'
import type {
  WorkFileIdentification,
  WorkFileIdentificationPrepareParams,
} from '~/entities/workFileIdentification'
import { byKeyComparer } from '~/utils/compare'
import { type Dayjs, parseDate } from '~/utils/date'

type Returning = T extends WorkFileIdentification
  ? WorkFileIdentification
  : Omit<WorkFileIdentificationPrepareParams, 'work_file_id'>

const props = defineProps<{
  organizationId: number
  locationId: number
  checkFileId: number
  identifications: T[]
  editMode?: boolean
}>()

const emit = defineEmits<{
  submit: [values: Returning[]]
}>()

const isActive = defineModel<boolean>('isActive', { required: true })

const { t } = useI18n()

const values = ref<WorkFileIdentificationPrepareParams[]>([])

function identificationFactory(from: T): WorkFileIdentificationPrepareParams {
  if ('value' in from) {
    // WorkFileIdentification をそのまま返す
    return { ...from }
  } else {
    // CheckFileIdentification から WorkFileIdentificationPrepareParams を作成
    return {
      value: '',
      check_file_identification_id: from.id,
      type: from.type,
      display_name: from.display_name,
      check_file_id: props.checkFileId,
      location_id: props.locationId,
      organization_id: props.organizationId,
      position: from.position,
    }
  }
}

watchEffect(() => {
  values.value = [...props.identifications]
    .sort(byKeyComparer('position'))
    .map(identificationFactory)
})

const setTextValue = (index: number, value: string) => {
  values.value[index].value = value
}

const showNumberInput = ref<number | false>(false)
const setNumberValue = (index: number, value: number | null) => {
  if (value === null) return
  values.value[index].value = value.toString()
  showNumberInput.value = false
}

const setDateValue = (index: number, value: Dayjs) => {
  values.value[index].value = value.isValid() ? value.format('YYYY-MM-DD') : ''
}

const isFilled = useArrayEvery(values, (v) => !!v.value)

const textErrorMessage = (value: string, displayName: string) => {
  const LENGTH_LIMIT = 40
  if (get2byteLength(value) > LENGTH_LIMIT) {
    return t('fieldは全角count文字以下で設定してください。', {
      field: displayName,
      count: LENGTH_LIMIT / 2,
    })
  }
  return undefined
}
// 数値の文字数はテンキーダイアログ側で制限されているため、ここではチェックしない
const hasError = useArraySome(
  values,
  (v) => v.type === 'TEXT' && !!textErrorMessage(v.value, v.display_name)
)

const canSubmit = computed(() => isFilled.value && !hasError.value)

const submit = () => {
  isActive.value = false
  emit('submit', values.value as Returning[])
}

const activeCameraIndex = ref<number | null>(null)

const openCamera = (index: number) => {
  activeCameraIndex.value = index
}

const handleCameraSubmit = (scannedValue: string) => {
  if (activeCameraIndex.value !== null) {
    values.value[activeCameraIndex.value].value = scannedValue
    activeCameraIndex.value = null
  }
}
</script>

<template>
  <BaseDialog
    v-model:is-active="isActive"
    :title="editMode ? $t('識別情報の変更') : $t('識別情報を入力')"
    :buttons="{
      ok: { text: editMode ? $t('保存') : $t('開始'), disabled: !canSubmit },
      cancel: { text: $t('キャンセル') },
    }"
    @cancel="isActive = false"
    @ok="submit()"
  >
    <template #content>
      <BaseStack gap="m">
        <p v-t="'今回のチェック内容を識別するための情報を記入してください。'" />
        <BaseStack gap="m">
          <BaseStack v-for="(v, i) in values" :key="v.check_file_identification_id">
            <BaseLabel :label="v.display_name" />
            <template v-if="v.type === 'TEXT'">
              <BaseInput
                :model-value="v.value"
                :error-message="textErrorMessage(v.value, v.display_name)"
                @update:model-value="setTextValue(i, $event)"
              >
                <template #suffix>
                  <BaseIconButton
                    icon-type="camera"
                    :hover-effect="false"
                    @click.stop.prevent="openCamera(i)"
                  />
                </template>
              </BaseInput>
            </template>
            <template v-if="v.type === 'NUMBER'">
              <BaseInput
                :model-value="v.value"
                readonly
                @click="showNumberInput = v.check_file_identification_id"
              >
                <template #suffix>
                  <BaseIconButton
                    icon-type="camera"
                    :hover-effect="false"
                    @click.stop.prevent="openCamera(i)"
                  />
                </template>
              </BaseInput>
              <IdentificationNumberInputDialog
                v-if="showNumberInput === v.check_file_identification_id"
                :is-active="showNumberInput === v.check_file_identification_id"
                :display-name="v.display_name"
                :initial-value="Number(v.value) || null"
                @update:is-active="
                  showNumberInput = $event ? v.check_file_identification_id : false
                "
                @submit-value="setNumberValue(i, $event)"
              />
            </template>
            <DatePicker
              v-if="v.type === 'DATE'"
              :model-value="parseDate(v.value)"
              @update:model-value="setDateValue(i, $event)"
            />
          </BaseStack>
        </BaseStack>

        <QRCodeReaderDialogConfirm
          v-if="activeCameraIndex !== null"
          :is-active="activeCameraIndex !== null"
          :type="values[activeCameraIndex].type"
          @update:is-active="activeCameraIndex = null"
          @submit="handleCameraSubmit"
        />
      </BaseStack>
    </template>
  </BaseDialog>
</template>

<style scoped lang="scss"></style>
